import React, { useState, useRef } from 'react';
import {
    Typography, Card, Button, Space, Form, Input, DatePicker,
    Switch, InputNumber, message, Alert, Divider, Tag, Row, Col,
    Modal, Progress, Radio, Select, Collapse, Tooltip
} from 'antd';
import dayjs from 'dayjs';
import {
    SaveOutlined, PlusOutlined, DeleteOutlined, CheckCircleFilled,
    LoadingOutlined, RobotOutlined, SendOutlined, FolderOutlined,
    BookOutlined, FileTextOutlined, CaretRightOutlined
} from '@ant-design/icons';
import { TrainingType, AINestedSubject, AINestedChapter, AINestedModule } from '../types/training';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { cpp } from '@codemirror/lang-cpp';
import { go } from '@codemirror/lang-go';
import { java } from '@codemirror/lang-java';
import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';
import { csharp } from '@replit/codemirror-lang-csharp';
import { EditorView } from '@codemirror/view';
import { createMoodleProgram, createMoodleQuiz } from '../services/moodleService';
import { adminGenerateAIQuestions, adminGenerateChapterModules } from '../services/adminService';
import { validateBlobSafeName } from '../utils/moduleUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUserEmail } from '../services/trainingService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

const genId = () => Math.random().toString(36).substr(2, 9);

const makeModule = (): AINestedModule => ({
    id: genId(), name: '', description: '', difficulty: 'Medium',
    questionCount: 5, estimatedTime: 30, attemptLimit: 0, deadline: 0,
    questions: [], generated: false, generating: false, extraPrompt: ''
});

const makeChapter = (): AINestedChapter => ({
    id: genId(), name: '', modules: [makeModule()]
});

const makeSubject = (): AINestedSubject => ({
    id: genId(), name: '', chapters: [makeChapter()]
});

// Helpers to update nested state immutably
const updateSubject = (subjects: AINestedSubject[], subjectId: string, updater: (s: AINestedSubject) => AINestedSubject): AINestedSubject[] =>
    subjects.map(s => s.id === subjectId ? updater(s) : s);

const updateChapter = (subjects: AINestedSubject[], subjectId: string, chapterId: string, updater: (c: AINestedChapter) => AINestedChapter): AINestedSubject[] =>
    updateSubject(subjects, subjectId, s => ({ ...s, chapters: s.chapters.map(c => c.id === chapterId ? updater(c) : c) }));

const updateModule = (subjects: AINestedSubject[], subjectId: string, chapterId: string, moduleId: string, updater: (m: AINestedModule) => AINestedModule): AINestedSubject[] =>
    updateChapter(subjects, subjectId, chapterId, c => ({ ...c, modules: c.modules.map(m => m.id === moduleId ? updater(m) : m) }));

const getAllModules = (subjects: AINestedSubject[]): { subject: AINestedSubject; chapter: AINestedChapter; mod: AINestedModule }[] => {
    const result: { subject: AINestedSubject; chapter: AINestedChapter; mod: AINestedModule }[] = [];
    subjects.forEach(s => s.chapters.forEach(c => c.modules.forEach(m => result.push({ subject: s, chapter: c, mod: m }))));
    return result;
};

const CreateAINestedProgram: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [subjects, setSubjects] = useState<AINestedSubject[]>([makeSubject()]);
    const [generatingChapterIds, setGeneratingChapterIds] = useState<Set<string>>(new Set());

    // Progress modal
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');

    // Question review modal
    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [reviewTarget, setReviewTarget] = useState<{ subjectId: string; chapterId: string; moduleId: string } | null>(null);

    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const publishIntentRef = useRef(false);
    const trainingName = Form.useWatch('trainingName', form);

    // ── Subject / Chapter / Module CRUD ──────────────────────────────────────

    const addSubject = () => setSubjects(prev => [...prev, makeSubject()]);
    const removeSubject = (id: string) => setSubjects(prev => prev.filter(s => s.id !== id));

    const addChapter = (subjectId: string) =>
        setSubjects(prev => updateSubject(prev, subjectId, s => ({ ...s, chapters: [...s.chapters, makeChapter()] })));
    const removeChapter = (subjectId: string, chapterId: string) =>
        setSubjects(prev => updateSubject(prev, subjectId, s => ({ ...s, chapters: s.chapters.filter(c => c.id !== chapterId) })));

    const addModuleToChapter = (subjectId: string, chapterId: string) =>
        setSubjects(prev => updateChapter(prev, subjectId, chapterId, c => ({ ...c, modules: [...c.modules, makeModule()] })));
    const removeModuleFromChapter = (subjectId: string, chapterId: string, moduleId: string) =>
        setSubjects(prev => updateChapter(prev, subjectId, chapterId, c => ({ ...c, modules: c.modules.filter(m => m.id !== moduleId) })));

    const updateModuleField = (subjectId: string, chapterId: string, moduleId: string, field: keyof AINestedModule, value: any) =>
        setSubjects(prev => updateModule(prev, subjectId, chapterId, moduleId, m => ({ ...m, [field]: value })));

    const updateSubjectName = (id: string, name: string) =>
        setSubjects(prev => updateSubject(prev, id, s => ({ ...s, name })));
    const updateChapterName = (subjectId: string, chapterId: string, name: string) =>
        setSubjects(prev => updateChapter(prev, subjectId, chapterId, c => ({ ...c, name })));

    // ── AI Module Structure Generation ───────────────────────────────────────

    const handleGenerateChapterModules = async (subjectId: string, chapterId: string) => {
        const subject = subjects.find(s => s.id === subjectId);
        const chapter = subject?.chapters.find(c => c.id === chapterId);
        if (!chapter?.name.trim()) {
            message.warning('Please enter a Chapter name before generating modules.');
            return;
        }
        const programName = form.getFieldValue('trainingName') || 'Training Program';
        const subjectName = subject?.name || '';
        setGeneratingChapterIds(prev => new Set(prev).add(chapterId));
        try {
            const generated = await adminGenerateChapterModules(programName, subjectName, chapter.name);
            if (!generated.length) throw new Error('No modules returned');
            const newModules: AINestedModule[] = generated.map(g => ({
                ...makeModule(), name: g.name, description: g.description
            }));
            setSubjects(prev => updateChapter(prev, subjectId, chapterId, c => ({
                ...c,
                modules: c.modules.length === 1 && !c.modules[0].name.trim()
                    ? newModules
                    : [...c.modules, ...newModules]
            })));
            message.success(`Generated ${newModules.length} modules for "${chapter.name}"`);
        } catch (err: any) {
            message.error(err.message || 'Failed to generate modules');
        } finally {
            setGeneratingChapterIds(prev => { const s = new Set(prev); s.delete(chapterId); return s; });
        }
    };

    // ── AI Question Generation ────────────────────────────────────────────────

    const handleGenerateForModule = async (subjectId: string, chapterId: string, mod: AINestedModule) => {
        const programName = form.getFieldValue('trainingName') || 'Training Program';
        if (!mod.name || !mod.description) {
            message.warning('Please provide a Module Name and Description before generating.');
            return;
        }
        setSubjects(prev => updateModule(prev, subjectId, chapterId, mod.id, m => ({ ...m, generating: true, generated: false, questions: [] })));
        try {
            const aiData = await adminGenerateAIQuestions(
                `Training Program: ${programName}. Module Name: ${mod.name}. Topic: ${mod.description}. Generate ${mod.questionCount} distinct questions.`,
                mod.difficulty,
                mod.questionCount,
                mod.extraPrompt
            );
            const formatted = aiData.questions.slice(0, mod.questionCount).map((q: any) => ({
                id: genId(),
                question: q.Question || q.question || '',
                answer: q.Answer || q.answer || '',
                codeSnippet: q.CodeSnippet || q.codeSnippet || '',
                language: (q.RequiredLanguage || q.requiredLanguage || 'javascript').toLowerCase()
            }));
            setSubjects(prev => updateModule(prev, subjectId, chapterId, mod.id, m => ({
                ...m, questions: formatted,
                estimatedTime: aiData.estimatedTimeMinutes || m.estimatedTime,
                generated: true, generating: false
            })));
            message.success(`Generated ${formatted.length} questions for "${mod.name}"`);
        } catch (err: any) {
            message.error(err.message || 'Failed to generate questions');
            setSubjects(prev => updateModule(prev, subjectId, chapterId, mod.id, m => ({ ...m, generating: false })));
        }
    };

    const [reviewGenerating, setReviewGenerating] = useState(false);
    const [reviewExtraPrompt, setReviewExtraPrompt] = useState('');
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);

    // ── Review modal helpers ──────────────────────────────────────────────────

    const autoHeightExt = EditorView.theme({
        '&': { height: 'auto' },
        '.cm-scroller': { overflow: 'visible' },
    });

    const getCodeMirrorExtensions = (lang: string) => {
        const langExt = (() => { switch ((lang || '').toLowerCase()) {
            case 'typescript': return javascript({ typescript: true });
            case 'python':     return python();
            case 'cpp':        return cpp();
            case 'csharp':     return csharp();
            case 'go':         return go();
            case 'java':       return java();
            case 'sql':        return sql();
            default:           return javascript({ jsx: true });
        }})();
        return [langExt, autoHeightExt];
    };

    const handleGenerateMoreQuestions = async () => {
        const mod = getReviewModule();
        if (!mod || !reviewTarget) return;
        const programName = form.getFieldValue('trainingName') || 'Training Program';
        const combinedExtra = [mod.extraPrompt, reviewExtraPrompt].filter(Boolean).join('. ');
        setReviewGenerating(true);
        try {
            const aiData = await adminGenerateAIQuestions(
                `Training Program: ${programName}. Module Name: ${mod.name}. Topic: ${mod.description}. Generate 1 distinct question.`,
                mod.difficulty, 1, combinedExtra
            );
            const newQuestions = aiData.questions.slice(0, 1).map((q: any) => ({
                id: genId(),
                question: q.Question || q.question || '',
                answer: q.Answer || q.answer || '',
                codeSnippet: q.CodeSnippet || q.codeSnippet || '',
                language: (q.RequiredLanguage || q.requiredLanguage || 'javascript').toLowerCase()
            }));
            setSubjects(prev => updateModule(prev, reviewTarget.subjectId, reviewTarget.chapterId, reviewTarget.moduleId,
                m => ({ ...m, questions: [...m.questions, ...newQuestions], generated: true })
            ));
            if (!selectedQuestionId) setSelectedQuestionId(newQuestions[0]?.id ?? null);
            message.success(`Added ${newQuestions.length} more questions`);
        } catch (err: any) {
            message.error(err.message || 'Failed to generate questions');
        } finally {
            setReviewGenerating(false);
        }
    };

    const openReviewModal = (subjectId: string, chapterId: string, moduleId: string) => {
        setReviewTarget({ subjectId, chapterId, moduleId });
        setReviewModalVisible(true);
        // auto-select first question
        const s = subjects.find(s => s.id === subjectId);
        const c = s?.chapters.find(c => c.id === chapterId);
        const mod = c?.modules.find(m => m.id === moduleId);
        setSelectedQuestionId(mod?.questions[0]?.id ?? null);
    };

    const getReviewModule = (): AINestedModule | null => {
        if (!reviewTarget) return null;
        const s = subjects.find(s => s.id === reviewTarget.subjectId);
        const c = s?.chapters.find(c => c.id === reviewTarget.chapterId);
        return c?.modules.find(m => m.id === reviewTarget.moduleId) ?? null;
    };

    const updateReviewQuestion = (qId: string, field: 'question' | 'answer' | 'codeSnippet' | 'language', value: string) => {
        if (!reviewTarget) return;
        setSubjects(prev => updateModule(prev, reviewTarget.subjectId, reviewTarget.chapterId, reviewTarget.moduleId,
            m => ({ ...m, questions: m.questions.map(q => q.id === qId ? { ...q, [field]: value } : q) })
        ));
    };

    const removeReviewQuestion = (qId: string) => {
        if (!reviewTarget) return;
        setSubjects(prev => {
            const updated = updateModule(prev, reviewTarget.subjectId, reviewTarget.chapterId, reviewTarget.moduleId,
                m => ({ ...m, questions: m.questions.filter(q => q.id !== qId) })
            );
            // select adjacent question after deletion
            const s = updated.find(s => s.id === reviewTarget.subjectId);
            const c = s?.chapters.find(c => c.id === reviewTarget.chapterId);
            const qs = c?.modules.find(m => m.id === reviewTarget.moduleId)?.questions ?? [];
            setSelectedQuestionId(qs[0]?.id ?? null);
            return updated;
        });
    };

    const addReviewQuestion = () => {
        if (!reviewTarget) return;
        const newId = genId();
        setSubjects(prev => updateModule(prev, reviewTarget.subjectId, reviewTarget.chapterId, reviewTarget.moduleId,
            m => ({ ...m, questions: [...m.questions, { id: newId, question: '', answer: '', codeSnippet: '', language: 'javascript' }] })
        ));
        setSelectedQuestionId(newId);
    };

    // ── Submit ────────────────────────────────────────────────────────────────

    const handleFinish = async (values: any) => {
        try {
            const allMods = getAllModules(subjects);
            if (allMods.length === 0) throw new Error('Add at least one module.');
            if (subjects.some(s => !s.name.trim())) throw new Error('All subjects must have a name.');
            if (subjects.some(s => s.chapters.some(c => !c.name.trim()))) throw new Error('All chapters must have a name.');
            if (allMods.some(({ mod }) => !mod.name.trim())) throw new Error('All modules must have a name.');
            const ungenerated = allMods.filter(({ mod }) => !mod.generated || mod.questions.length === 0);
            if (ungenerated.length > 0) throw new Error(`Generate questions for: ${ungenerated.map(u => u.mod.name).join(', ')}`);
            const missingDeadline = allMods.filter(({ mod }) => !mod.deadline || mod.deadline <= 0);
            if (missingDeadline.length > 0) throw new Error(`Set a submission deadline for: ${missingDeadline.map(u => u.mod.name).join(', ')}`);

            setTotalSteps(allMods.length + 1);
            setCurrentStep(0);
            setProcessStatus('active');
            setProgressMessage('Initializing...');
            setProgressModalVisible(true);
            setLoading(true);

            const cleanEmails = (s: string) => s ? s.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean).join(';') : '';

            // Build initial hierarchy (moodleIds filled in after quiz creation below)
            const moodleIdMap = new Map<string, number>(); // moduleId → Moodle quiz ID

            const buildHierarchy = () => subjects.map(s => ({
                id: s.id, name: s.name,
                chapters: s.chapters.map(c => ({
                    id: c.id, name: c.name,
                    modules: c.modules.map(m => ({
                        id: m.id, moodleId: moodleIdMap.get(m.id) || 0,
                        name: m.name, description: m.description,
                        difficulty: m.difficulty, questionCount: m.questionCount,
                        estimatedTime: m.estimatedTime, attemptLimit: m.attemptLimit,
                        deadline: m.deadline || 0
                    }))
                }))
            }));

            const trainingPayload = {
                trainingId: values.trainingId,
                trainingName: values.trainingName,
                active: true,
                startDate: values.isEvergreen ? null : values.dateRange[0].format('YYYY-MM-DD'),
                endDate: values.isEvergreen ? null : values.dateRange[1].format('YYYY-MM-DD'),
                passingPercentage: values.passingPercentage,
                trainingLink: values.trainingLink || '',
                estimatedEffortHours: values.estimatedEffortHours,
                audienceEmails: cleanEmails(values.audienceEmails),
                stakeholderEmails: cleanEmails(values.stakeholderEmails),
                assessments: JSON.stringify(buildHierarchy()),
                notificationVersion: values.notificationVersion || '1.0',
                trainingType: values.trainingType,
                programType: 'AINested',
                visible: publishIntentRef.current ? 1 : 0,
                createdby: await getCurrentUserEmail()
            };

            setProgressMessage('Creating program in Moodle...');
            const programResult = await createMoodleProgram(trainingPayload as any);
            const courseId = programResult.id;
            setCurrentStep(1);

            let quizIndex = 1;
            for (const { subject, chapter, mod } of allMods) {
                setProgressMessage(`Creating quiz: ${subject.name} › ${chapter.name} › ${mod.name} (${quizIndex}/${allMods.length})...`);
                const quizResult = await createMoodleQuiz(
                    courseId, mod.name,
                    `${subject.name} › ${chapter.name} › ${mod.name}. ${mod.description}`,
                    quizIndex, mod.questions, mod.estimatedTime || 30, mod.attemptLimit || 0, mod.deadline || 0
                );
                if (quizResult?.id) moodleIdMap.set(mod.id, quizResult.id);
                setCurrentStep(prev => prev + 1);
                quizIndex++;
            }

            // Update assessments with moodleIds now that all quizzes are created
            setProgressMessage('Saving quiz references...');
            await import('../services/adminService').then(({ adminUpdateTraining }) =>
                adminUpdateTraining(courseId, {
                    trainingName: values.trainingName,
                    trainingType: values.trainingType,
                    passingPercentage: values.passingPercentage,
                    estimatedEffortHours: values.estimatedEffortHours,
                    trainingLink: values.trainingLink || '',
                    audienceEmails: cleanEmails(values.audienceEmails),
                    stakeholderEmails: cleanEmails(values.stakeholderEmails),
                    assessments: JSON.stringify(buildHierarchy()),
                    notificationVersion: values.notificationVersion || '1.0',
                    programType: 'AINested'
                })
            );

            setProcessStatus('success');
            setProgressMessage(publishIntentRef.current
                ? 'Program published successfully! Redirecting...'
                : 'Draft saved! Redirecting...');
            setTimeout(() => navigate('/admin'), 1500);
        } catch (err: any) {
            setProcessStatus('exception');
            setProgressMessage(`Error: ${err.message || 'Failed to create program'}`);
            setLoading(false);
        }
    };

    // ── Module row renderer ───────────────────────────────────────────────────

    const renderModuleRow = (subjectId: string, chapterId: string, mod: AINestedModule, isOnlyModule: boolean) => (
        <Card
            key={mod.id}
            size="small"
            style={{ marginBottom: 8, borderRadius: 8, border: '1px solid #f0f0f0' }}
            bodyStyle={{ padding: '12px 16px' }}
        >
            <Row gutter={12} align="middle">
                <Col flex="auto">
                    <Row gutter={8}>
                        <Col xs={24} md={6}>
                            <Input
                                placeholder="Module name"
                                value={mod.name}
                                onChange={e => updateModuleField(subjectId, chapterId, mod.id, 'name', e.target.value)}
                                prefix={<FileTextOutlined style={{ color: '#8c8c8c' }} />}
                                size="small"
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Input
                                placeholder="Topic / description"
                                value={mod.description}
                                onChange={e => updateModuleField(subjectId, chapterId, mod.id, 'description', e.target.value)}
                                size="small"
                            />
                            <Input
                                placeholder="Extra prompt (optional)"
                                value={mod.extraPrompt}
                                onChange={e => updateModuleField(subjectId, chapterId, mod.id, 'extraPrompt', e.target.value)}
                                size="small"
                                style={{ marginTop: 4 }}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <Select
                                value={mod.difficulty}
                                onChange={val => updateModuleField(subjectId, chapterId, mod.id, 'difficulty', val)}
                                style={{ width: '100%' }}
                                size="small"
                            >
                                <Option value="Easy">Easy</Option>
                                <Option value="Medium">Med</Option>
                                <Option value="Hard">Hard</Option>
                            </Select>
                        </Col>
                        <Col xs={4} md={2}>
                            <InputNumber
                                min={1} max={20} value={mod.questionCount} size="small"
                                onChange={val => updateModuleField(subjectId, chapterId, mod.id, 'questionCount', val)}
                                style={{ width: '100%' }} placeholder="Qty"
                            />
                        </Col>
                        <Col xs={4} md={2}>
                            <InputNumber
                                min={1} max={300} value={mod.estimatedTime} size="small"
                                onChange={val => updateModuleField(subjectId, chapterId, mod.id, 'estimatedTime', val || 30)}
                                style={{ width: '100%' }} placeholder="Mins"
                            />
                        </Col>
                        <Col xs={4} md={2}>
                            <InputNumber
                                min={0} max={99} value={mod.attemptLimit} size="small"
                                onChange={val => updateModuleField(subjectId, chapterId, mod.id, 'attemptLimit', val ?? 0)}
                                style={{ width: '100%' }} placeholder="0=∞"
                            />
                        </Col>
                        <Col xs={24} md={4}>
                            <DatePicker
                                size="small"
                                showTime={{ format: 'HH:mm:ss', defaultValue: dayjs('23:59:59', 'HH:mm:ss') }}
                                format="D MMM YY HH:mm:ss"
                                placeholder="Select deadline"
                                value={mod.deadline ? dayjs.unix(mod.deadline) : null}
                                onChange={val => updateModuleField(subjectId, chapterId, mod.id, 'deadline', val ? val.unix() : 0)}
                                style={{ width: '100%' }}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col>
                    <Space>
                        <Button
                            size="small"
                            type={mod.generated ? 'default' : 'primary'}
                            icon={<RobotOutlined />}
                            loading={mod.generating}
                            onClick={() => handleGenerateForModule(subjectId, chapterId, mod)}
                            disabled={getAllModules(subjects).some(x => x.mod.generating) && !mod.generating}
                        >
                            {mod.generating ? 'Generating' : mod.generated ? 'Regenerate' : 'Generate'}
                        </Button>
                        {mod.generated && (
                            <>
                                <Tag color="success" icon={<CheckCircleFilled />}>{mod.questions.length} Qs</Tag>
                                <Button type="link" size="small" onClick={() => openReviewModal(subjectId, chapterId, mod.id)}>Review</Button>
                            </>
                        )}
                        <Tooltip title={isOnlyModule ? 'At least one module required' : 'Remove module'}>
                            <Button
                                type="text" danger size="small" icon={<DeleteOutlined />}
                                disabled={isOnlyModule}
                                onClick={() => removeModuleFromChapter(subjectId, chapterId, mod.id)}
                            />
                        </Tooltip>
                    </Space>
                </Col>
            </Row>
        </Card>
    );

    const percent = Math.round((currentStep / Math.max(totalSteps, 1)) * 100);
    const reviewMod = getReviewModule();
    const allMods = getAllModules(subjects);
    const anyGenerating = allMods.some(x => x.mod.generating);

    return (
        <div>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{ active: true, passingPercentage: 80, notificationVersion: '1.0', trainingType: TrainingType.OPEN, isEvergreen: true }}
            >
                {/* ── Program Details ── */}
                <Card title="1. Program Details" style={{ marginBottom: 24, borderRadius: 12 }}>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Training ID" name="trainingId" rules={[{ required: true }, { validator: validateBlobSafeName }]}>
                                <Input placeholder="TR-202X-001" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Passing %" name="passingPercentage" rules={[{ required: true }]}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }}
                                    formatter={v => v ? `${v}%` : ''} parser={v => v ? parseFloat(v.replace('%', '')) : 0 as any} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Assessment Duration (Hours)" name="estimatedEffortHours">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Training Program Name" name="trainingName" rules={[{ required: true }]}>
                        <Input placeholder="e.g. Fresher Batch - Feb 2026" size="large" maxLength={250} showCount />
                    </Form.Item>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.trainingType !== cur.trainingType || prev.isEvergreen !== cur.isEvergreen}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const type = getFieldValue('trainingType');
                                    const evergreen = getFieldValue('isEvergreen');
                                    if (type !== TrainingType.OPEN && evergreen) setTimeout(() => setFieldsValue({ isEvergreen: false }), 0);
                                    return (
                                        <>
                                            {type === TrainingType.OPEN && (
                                                <Form.Item name="isEvergreen" valuePropName="checked" style={{ marginBottom: 16 }}>
                                                    <Switch checkedChildren="Evergreen (No Fixed Dates)" unCheckedChildren="Fixed Dates" />
                                                </Form.Item>
                                            )}
                                            {(!evergreen || type !== TrainingType.OPEN) ? (
                                                <Form.Item label="Training Period" name="dateRange" rules={[{ required: true, message: 'Select a period' }]}>
                                                    <DatePicker.RangePicker style={{ width: '100%' }} size="large" />
                                                </Form.Item>
                                            ) : (
                                                <Alert message="Evergreen Program" description="No fixed dates — available immediately." type="info" showIcon style={{ marginBottom: 24 }} />
                                            )}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Resource / Documentation Link" name="trainingLink">
                                <TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="https://..." />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider />
                    <Form.Item label="Training Accessibility Type" name="trainingType" rules={[{ required: true }]}>
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={TrainingType.OPEN}><Text strong>Open for All</Text></Radio>
                                <Radio value={TrainingType.REQUESTABLE}><Text strong>Nominated + Request to Join</Text></Radio>
                                <Radio value={TrainingType.PRIVATE}><Text strong>Private (Invite Only)</Text></Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.trainingType !== cur.trainingType}>
                        {({ getFieldValue }) => {
                            if (getFieldValue('trainingType') === TrainingType.OPEN) return null;
                            return (
                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Participants (semicolon separated)" name="audienceEmails" rules={[{ required: true }]}>
                                            <TextArea rows={3} placeholder="user1@company.com; user2@company.com" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Stakeholders (semicolon separated)" name="stakeholderEmails" rules={[{ required: true }]}>
                                            <TextArea rows={3} placeholder="manager@company.com" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            );
                        }}
                    </Form.Item>

                </Card>

                {/* ── Hierarchy Builder ── */}
                <Card
                    title="2. Learning Hierarchy"
                    style={{ marginBottom: 24, borderRadius: 12 }}
                    extra={
                        <Button icon={<PlusOutlined />} onClick={addSubject} type="dashed">Add Subject</Button>
                    }
                >
                    <Alert
                        message="Structure: Subject → Chapter → Module (each module gets an AI-graded assessment)"
                        type="info" showIcon style={{ marginBottom: 16 }}
                    />
                    {subjects.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 32 }}>
                            <Text type="secondary">No subjects yet. Click "Add Subject" to start.</Text>
                        </div>
                    )}
                    <Collapse
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        defaultActiveKey={subjects.map(s => s.id)}
                    >
                        {subjects.map(subject => (
                            <Panel
                                key={subject.id}
                                header={
                                    <Space onClick={e => e.stopPropagation()}>
                                        <FolderOutlined style={{ color: '#fa541c' }} />
                                        <Input
                                            placeholder="Subject name (e.g. C++)"
                                            value={subject.name}
                                            onChange={e => updateSubjectName(subject.id, e.target.value)}
                                            style={{ width: 280, fontWeight: 600 }}
                                            onClick={e => e.stopPropagation()}
                                        />
                                        <Tag color="orange">{subject.chapters.length} chapter{subject.chapters.length !== 1 ? 's' : ''}</Tag>
                                    </Space>
                                }
                                extra={
                                    <Tooltip title={subjects.length === 1 ? 'At least one subject required' : 'Remove subject'}>
                                        <Button
                                            type="text" danger size="small" icon={<DeleteOutlined />}
                                            disabled={subjects.length === 1}
                                            onClick={e => { e.stopPropagation(); removeSubject(subject.id); }}
                                        />
                                    </Tooltip>
                                }
                            >
                                <Collapse
                                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                                    defaultActiveKey={subject.chapters.map(c => c.id)}
                                    style={{ background: '#fafafa', borderRadius: 8 }}
                                >
                                    {subject.chapters.map(chapter => (
                                        <Panel
                                            key={chapter.id}
                                            header={
                                                <Space onClick={e => e.stopPropagation()}>
                                                    <BookOutlined style={{ color: '#1677ff' }} />
                                                    <Input
                                                        placeholder="Chapter name (e.g. C++ Basic)"
                                                        value={chapter.name}
                                                        onChange={e => updateChapterName(subject.id, chapter.id, e.target.value)}
                                                        style={{ width: 260 }}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                    <Tag color="blue">{chapter.modules.length} module{chapter.modules.length !== 1 ? 's' : ''}</Tag>
                                                </Space>
                                            }
                                            extra={
                                                <Space onClick={e => e.stopPropagation()}>
                                                    <Button
                                                        size="small" icon={<RobotOutlined />}
                                                        loading={generatingChapterIds.has(chapter.id)}
                                                        onClick={e => { e.stopPropagation(); handleGenerateChapterModules(subject.id, chapter.id); }}
                                                    >
                                                        Generate Modules
                                                    </Button>
                                                    <Tooltip title={subject.chapters.length === 1 ? 'At least one chapter required' : 'Remove chapter'}>
                                                        <Button
                                                            type="text" danger size="small" icon={<DeleteOutlined />}
                                                            disabled={subject.chapters.length === 1}
                                                            onClick={e => { e.stopPropagation(); removeChapter(subject.id, chapter.id); }}
                                                        />
                                                    </Tooltip>
                                                </Space>
                                            }
                                        >
                                            {/* Column headers */}
                                            <Row gutter={12} style={{ marginBottom: 4, paddingLeft: 8 }}>
                                                <Col flex="auto">
                                                    <Row gutter={8}>
                                                        <Col md={6}><Text type="secondary" style={{ fontSize: 11 }}>Module Name</Text></Col>
                                                        <Col md={6}><Text type="secondary" style={{ fontSize: 11 }}>Topic / Description</Text></Col>
                                                        <Col md={2}><Text type="secondary" style={{ fontSize: 11 }}>Level</Text></Col>
                                                        <Col md={2}><Text type="secondary" style={{ fontSize: 11 }}>Qty</Text></Col>
                                                        <Col md={2}><Text type="secondary" style={{ fontSize: 11 }}>Mins</Text></Col>
                                                        <Col md={2}><Text type="secondary" style={{ fontSize: 11 }}>Att.</Text></Col>
                                                        <Col md={4}><Text type="secondary" style={{ fontSize: 11 }}>Submission Deadline</Text></Col>
                                                    </Row>
                                                </Col>
                                                <Col><Text type="secondary" style={{ fontSize: 11 }}>Actions</Text></Col>
                                            </Row>

                                            {chapter.modules.map(mod => renderModuleRow(subject.id, chapter.id, mod, chapter.modules.length === 1))}

                                            <Button
                                                type="dashed" size="small" icon={<PlusOutlined />}
                                                onClick={() => addModuleToChapter(subject.id, chapter.id)}
                                                style={{ marginTop: 4 }}
                                            >
                                                Add Module
                                            </Button>
                                        </Panel>
                                    ))}
                                </Collapse>
                                <Button
                                    type="dashed" size="small" icon={<PlusOutlined />}
                                    onClick={() => addChapter(subject.id)}
                                    style={{ marginTop: 8 }}
                                >
                                    Add Chapter
                                </Button>
                            </Panel>
                        ))}
                    </Collapse>
                </Card>

                {/* ── Actions ── */}
                <Card style={{ borderRadius: 12 }}>
                    <Row justify="end">
                        <Space>
                            <Button size="large" onClick={() => navigate('/admin')}>Cancel</Button>
                            <Button size="large" icon={<SaveOutlined />} loading={loading}
                                onClick={() => { publishIntentRef.current = false; form.submit(); }}>
                                Save Draft
                            </Button>
                            <Button type="primary" size="large" icon={<SendOutlined />} loading={loading}
                                onClick={() => { publishIntentRef.current = true; form.submit(); }}>
                                Publish Program
                            </Button>
                        </Space>
                    </Row>
                </Card>
            </Form>

            {/* ── Progress Modal ── */}
            <Modal
                title={<Space>{processStatus === 'active' && <LoadingOutlined />}<span>{processStatus === 'active' ? 'Creating Program...' : 'Done'}</span></Space>}
                open={progressModalVisible}
                closable={processStatus === 'exception'}
                maskClosable={false}
                footer={processStatus === 'exception' ? [<Button key="close" onClick={() => setProgressModalVisible(false)}>Close</Button>] : null}
            >
                <Progress type="circle" percent={percent} status={processStatus} style={{ display: 'block', textAlign: 'center', marginBottom: 16 }} />
                <Text style={{ display: 'block', textAlign: 'center' }}>{progressMessage}</Text>
            </Modal>

            {/* ── Review Modal ── */}
            <Modal
                title={`Review Questions — ${reviewMod?.name || ''} (${reviewMod?.questions.length ?? 0} questions)`}
                open={reviewModalVisible}
                onCancel={() => setReviewModalVisible(false)}
                footer={[
                    <Button key="add" icon={<PlusOutlined />} onClick={addReviewQuestion}>Add Question</Button>,
                    <Button key="gen" icon={<RobotOutlined />} loading={reviewGenerating} onClick={handleGenerateMoreQuestions}>Generate More</Button>,
                    <Button key="close" type="primary" onClick={() => setReviewModalVisible(false)}>Done</Button>
                ]}
                width="90vw"
                styles={{ body: { padding: 0 } }}
            >
                <Row style={{ height: '72vh' }}>
                    {/* ── Left: Question List ── */}
                    <Col span={7} style={{ borderRight: '1px solid #f0f0f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '12px', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>Additional instructions for Generate More</Text>
                            <TextArea
                                rows={2}
                                value={reviewExtraPrompt}
                                onChange={e => setReviewExtraPrompt(e.target.value)}
                                placeholder='"Focus on code snippets", "Bug-fix questions"...'
                                style={{ fontSize: 12, marginTop: 4 }}
                            />
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                            {(!reviewMod || reviewMod.questions.length === 0) && (
                                <Alert message="No questions yet. Generate or add manually." type="warning" showIcon style={{ margin: 8 }} />
                            )}
                            {reviewMod?.questions.map((q, idx) => {
                                const isSelected = selectedQuestionId === q.id;
                                return (
                                    <div
                                        key={q.id}
                                        onClick={() => setSelectedQuestionId(q.id)}
                                        style={{
                                            padding: '8px 10px',
                                            marginBottom: 4,
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            background: isSelected ? '#e6f4ff' : 'transparent',
                                            border: isSelected ? '1px solid #91caff' : '1px solid transparent',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Text strong style={{ fontSize: 12, color: isSelected ? '#1677ff' : undefined }}>Q{idx + 1}</Text>
                                            <Button type="text" danger size="small" icon={<DeleteOutlined />}
                                                style={{ padding: '0 4px', height: 20 }}
                                                onClick={e => { e.stopPropagation(); removeReviewQuestion(q.id); }} />
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {q.question || '(empty)'}
                                        </Text>
                                    </div>
                                );
                            })}
                        </div>
                    </Col>

                    {/* ── Right: Question Detail ── */}
                    <Col span={17} style={{ height: '100%', overflowY: 'auto', padding: '16px 20px' }}>
                        {(() => {
                            const q = reviewMod?.questions.find(q => q.id === selectedQuestionId);
                            if (!q) return (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <Text type="secondary">Select a question from the list to view and edit</Text>
                                </div>
                            );
                            return (
                                <>
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Question</Text>
                                        <TextArea
                                            autoSize={{ minRows: 3, maxRows: 8 }}
                                            value={q.question}
                                            onChange={e => updateReviewQuestion(q.id, 'question', e.target.value)}
                                        />
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong style={{ display: 'block', marginBottom: 6 }}>Reference Answer</Text>
                                        <TextArea
                                            autoSize={{ minRows: 3, maxRows: 8 }}
                                            value={q.answer}
                                            onChange={e => updateReviewQuestion(q.id, 'answer', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Space size={8} style={{ marginBottom: 6 }}>
                                            <Text strong>Code Snippet (optional)</Text>
                                            <Select size="small" value={q.language || 'javascript'} style={{ width: 115 }}
                                                onChange={val => updateReviewQuestion(q.id, 'language', val)}>
                                                <Option value="javascript">JavaScript</Option>
                                                <Option value="typescript">TypeScript</Option>
                                                <Option value="python">Python</Option>
                                                <Option value="java">Java</Option>
                                                <Option value="cpp">C++</Option>
                                                <Option value="csharp">C#</Option>
                                                <Option value="go">Go</Option>
                                                <Option value="sql">SQL</Option>
                                            </Select>
                                        </Space>
                                        <CodeMirror
                                            value={q.codeSnippet}
                                            theme={oneDark}
                                            extensions={getCodeMirrorExtensions(q.language || 'javascript')}
                                            onChange={val => updateReviewQuestion(q.id, 'codeSnippet', val)}
                                            basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true }}
                                        />
                                    </div>
                                </>
                            );
                        })()}
                    </Col>
                </Row>
            </Modal>
        </div>
    );
};

export default CreateAINestedProgram;
